import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { motion } from 'framer-motion';
import { metaPixelTrackViewContent } from '../../utils/metaPixel';
import { appendLangParam, signupHrefForLocale } from '../../utils/signupLangHref';

interface MarketingCTAProps {
  label?: string;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'md' | 'lg' | 'xl';
  href?: string;
  onClick?: () => void;
  /** Appelé en plus du tracking Meta, sans court-circuiter la navigation. */
  onTrack?: () => void;
  scrollTo?: string;
  trackEvent?: string;
  className?: string;
  fullWidth?: boolean;
  style?: React.CSSProperties;
}

export default function MarketingCTA({
  label,
  variant = 'primary',
  size = 'lg',
  href,
  onClick,
  onTrack,
  scrollTo,
  trackEvent,
  className = '',
  fullWidth = false,
  style = {},
}: MarketingCTAProps) {
  const { t, i18n } = useTranslation('marketing');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [hoverLift, setHoverLift] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(hover: hover)');
    const update = () => setHoverLift(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const handleClick = () => {
    if (trackEvent) {
      metaPixelTrackViewContent({ content_name: trackEvent });
    }
    onTrack?.();
    if (onClick) {
      onClick();
      return;
    }
    if (scrollTo) {
      const el = document.getElementById(scrollTo);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }
    if (href) {
      if (href.startsWith('http')) {
        window.open(href, '_blank');
      } else {
        const target =
          href.startsWith('/signup-user') || href.startsWith('/login')
            ? appendLangParam(href, i18n.language)
            : href;
        navigate(target);
      }
      return;
    }
    navigate(isAuthenticated ? '/app' : signupHrefForLocale(i18n.language));
  };

  const sizeStyles: Record<string, React.CSSProperties> = {
    md: { padding: '12px 28px', fontSize: '15px' },
    lg: { padding: '16px 36px', fontSize: '17px' },
    xl: { padding: '20px 44px', fontSize: '18px' },
  };

  const baseStyles: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontFamily: 'var(--mk-font-heading)',
    fontWeight: 700,
    borderRadius: 'var(--mk-radius-button)',
    cursor: 'pointer',
    border: 'none',
    transition: 'all var(--mk-transition-base)',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    width: fullWidth ? '100%' : undefined,
    ...sizeStyles[size],
  };

  const variantStyles: Record<string, React.CSSProperties> = {
    primary: {
      background: 'var(--mk-gradient-cta)',
      color: '#fff',
      boxShadow: 'var(--mk-shadow-md)',
    },
    secondary: {
      background: 'var(--mk-bg-card)',
      color: 'var(--mk-brown)',
      border: '2px solid var(--mk-brown)',
    },
    outline: {
      background: 'transparent',
      color: 'var(--mk-brown)',
      border: '2px solid rgba(165, 118, 92, 0.2)',
    },
  };

  return (
    <motion.button
      className={`mk-cta mk-cta--${variant} ${className}`}
      style={{ ...baseStyles, ...variantStyles[variant], ...style }}
      onClick={handleClick}
      whileHover={
        hoverLift
          ? {
              y: -4,
              scale: 1.02,
              boxShadow: '0 20px 40px rgba(255, 184, 76, 0.25)',
            }
          : undefined
      }
      whileTap={{ scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 400, damping: 15 }}
    >
      {label ?? t('marketingCta.defaultLabel')}
    </motion.button>
  );
}
